import { $, mainPanel, mainContent } from './dom.js';

/** Manages zoom and pan transformations on an SVG/content panel. Supports mouse-wheel
 * zoom (with optional Ctrl-key zoom), touchpad two-finger pan, left-click drag with
 * distance callbacks, and right-click drag-to-pan. */
export class ZoomPan {
  zoom: number = 1;
  panX: number = 0;
  panY: number = 0;
  onClick: ((e: MouseEvent) => void) | null = null;
  onDrag: ((screenDist: number) => void) | null = null;
  onDragEnd: (() => void) | null = null;
  onDown: ((e: MouseEvent) => void) | null = null;

  private _drag: boolean = false;
  private _clicking: boolean = false;
  private _clickDist: number = 0;
  private _clickMX: number = 0;
  private _clickMY: number = 0;
  private _clickOriginX: number = 0;
  private _clickOriginY: number = 0;
  private _mx: number = 0;
  private _my: number = 0;
  private _gesturePan: boolean = false;
  private _gestureTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(panel: HTMLElement, content: HTMLElement) {
    // Wheel: mouse wheel (deltaMode=0) -> zoom, touchpad scroll (deltaMode=1) -> pan
    panel.addEventListener('wheel', (e: WheelEvent) => {
      e.preventDefault();
      const r = panel.getBoundingClientRect();

      if (e.ctrlKey) {
        this._gesturePan = false;
        const f = e.deltaY > 0 ? 0.95 : 1.05;
        const mx = e.clientX - r.left, my = e.clientY - r.top;
        this.panX = mx - (mx - this.panX) * f;
        this.panY = my - (my - this.panY) * f;
        this.zoom = Math.max(0.05, Math.min(50, this.zoom * f));
        this.apply();
        return;
      }

      if (Math.abs(e.deltaX) > 0.5) this._gesturePan = true;
      if (this._gestureTimer) clearTimeout(this._gestureTimer);
      this._gestureTimer = setTimeout(() => { this._gesturePan = false; }, 200);

      if (this._gesturePan) {
        this.panX -= e.deltaX;
        this.panY -= e.deltaY;
      } else {
        const f = e.deltaY > 0 ? 0.9 : 1.1;
        const mx = e.clientX - r.left, my = e.clientY - r.top;
        this.panX = mx - (mx - this.panX) * f;
        this.panY = my - (my - this.panY) * f;
        this.zoom = Math.max(0.05, Math.min(50, this.zoom * f));
      }
      this.apply();
    }, { passive: false });

    panel.addEventListener('contextmenu', (e: Event) => e.preventDefault());

    content.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button === 0) {
        this._clickMX = e.clientX; this._clickMY = e.clientY;
        this._clickOriginX = e.clientX; this._clickOriginY = e.clientY;
        this._clickDist = 0;
        this._clicking = true;
        if (this.onDown) this.onDown(e);
        return;
      }
      if (e.button === 2) {
        this._drag = true;
        this._mx = e.clientX; this._my = e.clientY;
      }
    });

    window.addEventListener('mousemove', (e: MouseEvent) => {
      if (this._clicking) {
        const dx = e.clientX - this._clickMX, dy = e.clientY - this._clickMY;
        this._clickDist += Math.abs(dx) + Math.abs(dy);
        this._clickMX = e.clientX; this._clickMY = e.clientY;
        if (this._clickDist >= 5 && this.onDrag) {
          const odx = e.clientX - this._clickOriginX;
          const ody = e.clientY - this._clickOriginY;
          this.onDrag(Math.sqrt(odx * odx + ody * ody));
        }
      }
      if (!this._drag) return;
      const dx = e.clientX - this._mx, dy = e.clientY - this._my;
      this.panX += dx; this.panY += dy;
      this._mx = e.clientX; this._my = e.clientY;
      this.apply();
    });

    window.addEventListener('mouseup', (e: MouseEvent) => {
      if (this._clicking && e.button === 0) {
        const wasClick = this._clickDist < 5;
        this._clicking = false;
        if (wasClick && this.onClick) this.onClick(e);
        if (!wasClick && this.onDragEnd) this.onDragEnd();
      }
      if (this._drag && e.button === 2) {
        this._drag = false;
      }
    });
  }

  /** Apply the current zoom and pan values to the DOM transform of the content element. */
  apply(): void {
    mainContent.style.transform = `translate(${this.panX}px,${this.panY}px) scale(${this.zoom})`;
  }

  /** Reset zoom to 1x and pan to the origin, then re-apply. */
  reset(): void {
    this.zoom = 1; this.panX = 0; this.panY = 0;
    this.apply();
  }

  /** Scale and center content of size (w x h) to fit within the main panel, capped at 1x zoom.
   * @param w - Content width in pixels.
   * @param h - Content height in pixels. */
  fitToPanel(w: number, h: number): void {
    const r = mainPanel.getBoundingClientRect();
    const sx = r.width / w, sy = r.height / h;
    this.zoom = Math.min(sx, sy, 1);
    this.panX = (r.width - w * this.zoom) / 2;
    this.panY = (r.height - h * this.zoom) / 2;
    this.apply();
  }
}
